import React, { useMemo } from 'react';

import { Button as ChakraButton, useTheme } from '@chakra-ui/react';
import styled from '@emotion/styled';

import { Cross } from 'assets/assetsV1/iconComponents';

import { TextLabel, TextBodyMedium } from 'uiToolkitV1/Typography';

const TAG_VARIANT = {
  filled: 'filled',
  // outlined: 'outlined',
  tertiarySideNav: 'tertiarySideNav',
};

const TAG_SIZE = {
  // special case cause consistency and coherence in design is shit
  sideNav: 'sideNav',
  regular: 'regular',
  large: 'large',
  xLarge: 'xLarge',
};

const SIZE_TO_HEIGHT = {
  [TAG_SIZE.regular]: '20px',
  [TAG_SIZE.large]: '24px',
  [TAG_SIZE.xLarge]: '28px',
  [TAG_SIZE.sideNav]: '24px',
};

const TAG_COLORS = {
  blue: 'blue',
  red: 'red',
  grey: 'grey',
};

const TransientProps = ['size', 'isIconOnlyButton', 'isLeftIcon', 'buttonColor', 'isRightIcon', 'isTextOnlyButton'];

const getBaseButtonStyles = (theme, props) => ({
  boxShadow: 'none',

  height: SIZE_TO_HEIGHT[props.size],
  textTransform: 'none',
  padding: props.isTextOnlyButton ? '0px' : '2px',
  borderRadius: '2px',
  border: 0,
  minWidth: 'auto',
  width: props.isIconOnlyButton ? SIZE_TO_HEIGHT[props.size] : 'auto',
  '& .chakra-button__icon': {
    margin: '0px',
  },
  '&:hover:enabled': {
    cursor: 'pointer',
    backgroundColor: theme.colors.button[props.buttonColor].hover,
    '& svg': {
      color: theme.colors.icon.white,
    },
  },

  '&:active:enabled, &.active-styles': {
    backgroundColor: theme.colors.button[props.buttonColor].focus,
    '& svg': {
      color: theme.colors.icon.white,
    },
  },

  '&:disabled': {
    backgroundColor: theme.colors.button[props.buttonColor].disabled,
    opacity: '1',
    '&>*': {
      opacity: '50%',
    },
  },
});

const StyledButton = styled(ChakraButton, {
  shouldForwardProp: (prop) => !TransientProps.includes(prop),
})(({ theme, ...props }) => ({
  ...getBaseButtonStyles(theme, props),
  backgroundColor: theme.colors.button[props.buttonColor].default,
  '&:hover:disabled': {
    backgroundColor: theme.colors.button[props.buttonColor].disabled,
  },
}));

const TertiarySideNavButton = styled(ChakraButton, {
  shouldForwardProp: (prop) => !TransientProps.includes(prop),
})(({ theme, ...props }) => ({
  ...getBaseButtonStyles(theme, props),
  backgroundColor: 'transparent',
  '&>p': {
    color: theme.colors.button[props.buttonColor].default,
  },
  '& svg': {
    color: theme.colors.button[props.buttonColor].default,
  },
  '&:hover:enabled': {
    backgroundColor: 'transparent',
    cursor: 'pointer',
    '&>p': {
      color: theme.colors.button[props.buttonColor].hover,
    },
    '& svg': {
      color: theme.colors.button[props.buttonColor].hover,
    },
  },
  '&:active:enabled, &.active-styles': {
    '&>p': {
      color: theme.colors.button[props.buttonColor].focus,
    },
    '& svg': {
      color: theme.colors.button[props.buttonColor].focus,
    },
  },
  '&:disabled': {
    '&>p': {
      color: theme.colors.button[props.buttonColor].disabled,
    },
    '& svg': {
      color: theme.colors.button[props.buttonColor].disabled,
    },
    opacity: '1',
    '&>*': {
      opacity: '50%',
    },
  },
}));

export const Tag = ({
  children,
  variant = TAG_VARIANT.filled,
  size = TAG_SIZE.regular,
  StartIcon,
  showCloseButton, // <- need to get close icon
  // selected means render in active state, used in
  selected = false,
  className = '',
  buttonColor = TAG_COLORS.blue,
  ...props
}) => {
  let ButtonComp = null;
  let StartIconComp;
  let EndIconComp;
  let buttonSize = null;
  let iconSize = null;
  let TextLabelComp = null;
  const isIconOnlyButton = !children;
  const isLeftIcon = !!StartIcon;
  const isRightIcon = !!showCloseButton;
  const isTextOnlyButton = !StartIcon && !showCloseButton;

  const theme = useTheme();

  switch (variant) {
    case TAG_VARIANT.tertiarySideNav:
      ButtonComp = TertiarySideNavButton;
      break;
    case TAG_VARIANT.filled:
      ButtonComp = StyledButton;
      break;
    default:
      ButtonComp = StyledButton;
      console.error(`Error: Invalid button variant : ${variant} . Pick one from [${Object.keys(TAG_VARIANT)}]`);
  }

  switch (size) {
    case TAG_SIZE.regular:
      TextLabelComp = TextLabel;
      iconSize = '20px';
      buttonSize = TAG_SIZE.regular;

      break;
    case TAG_SIZE.large:
      TextLabelComp = TextBodyMedium;
      iconSize = '24px';
      buttonSize = TAG_SIZE.large;

      break;
    case TAG_SIZE.xLarge:
      TextLabelComp = TextBodyMedium;
      iconSize = '24px';
      buttonSize = TAG_SIZE.xLarge;
      break;

    case TAG_SIZE.sideNav:
      TextLabelComp = TextLabel;
      iconSize = '24px';
      buttonSize = TAG_SIZE.sideNav;

      break;
    default:
      console.error(`Error: Invalid tag size : ${size} . Pick one from [${Object.keys(TAG_SIZE)}]`);
      TextLabelComp = TextLabel;
      iconSize = '20px';
      buttonSize = TAG_SIZE.regular;
  }

  if (StartIcon) {
    StartIconComp = <StartIcon color={theme.colors.icon.white} boxSize={iconSize} p="2px" />;
  }

  if (showCloseButton) {
    EndIconComp = <Cross color={theme.colors.icon.white} boxSize={iconSize} p="2px" />;
  }

  const labelMargins = useMemo(() => {
    if (variant === TAG_VARIANT.tertiarySideNav) return '0 2px';
    if (!children) {
      // in case of icon only button
      return 0;
    }
    if (isTextOnlyButton) {
      // in case of text only button
      return '0 8px';
    }
    if (isLeftIcon && !isRightIcon) {
      return '0 8px';
    }
    if (!isLeftIcon && isRightIcon) {
      return '0 8px';
    }
    // showCloseButton case
    return '0 8px 0 8px';
  }, [children, isTextOnlyButton, isLeftIcon, isRightIcon]);
  return (
    <ButtonComp
      {...props}
      size={buttonSize}
      leftIcon={StartIconComp}
      rightIcon={EndIconComp}
      isIconOnlyButton={isIconOnlyButton}
      isLeftIcon={isLeftIcon}
      isRightIcon={isRightIcon}
      isTextOnlyButton={!isLeftIcon && !isRightIcon && children}
      className={selected ? `${className} active-styles` : className}
      buttonColor={buttonColor}
    >
      <TextLabelComp bold color="white" m={labelMargins}>
        {children}
      </TextLabelComp>
    </ButtonComp>
  );
};
